// frontend/src/lib/entitlements.js  (BL-041 — Subscription Entitlements Framework)
//
// FRONTEND mirror of the authoritative backend evaluator
// (netlify/functions/_shared/entitlements.js). ADVISORY only — drives UX
// (PaywallGate/Banner, upgrade prompts, DevSubscriptionPanel). The backend
// is the security boundary; this never enforces.
//
// Kept as PURE ESM (no React, no CRA-only imports) so the backend parity
// test can import it directly and lock FE/BE drift (NFR-4).
//
// Hard rules: canonical tiers free|investor|professional|enterprise — never
// `pro`; trial is a STATUS not a tier; fail-closed defaults.
//
// ⚠️  This file MUST stay in lockstep with the backend catalog. The parity
//     guard backend/tests/entitlementParity.test.js fails Gate 1 on drift.

// >>> BL-041 PARITY REGION START — keep byte-identical (modulo comments) with
//     netlify/functions/_shared/entitlements.js. Guarded by entitlementParity.test.js.
const DAY_MS = 1000 * 60 * 60 * 24;

export const CANONICAL_TIERS = Object.freeze(["free", "investor", "professional", "enterprise"]);

export const TIERS = Object.freeze({
  FREE: "free",
  INVESTOR: "investor",
  PROFESSIONAL: "professional",
  ENTERPRISE: "enterprise",
});

export const TIER_RANK = Object.freeze({ free: 0, investor: 1, professional: 2, enterprise: 3 });

export const CAPABILITIES = Object.freeze({
  deal_analyzer:    { minTier: "investor",     trialGrants: true,  requiresFlag: null },
  lease_leverage:   { minTier: "professional", trialGrants: false, requiresFlag: null },
  discovery_engine: { minTier: "free",         trialGrants: false, requiresFlag: "discovery_engine_enabled" },
  pdf_export:       { minTier: "free",         trialGrants: false, requiresFlag: "pdf_export_enabled" },
  ai_assistant:     { minTier: "professional", trialGrants: false, requiresFlag: "ai_assistant_enabled" },
});

export const LIMIT_CATEGORIES = Object.freeze(["analysis_volume"]);

export const TIER_LIMITS = Object.freeze({
  free:         Object.freeze({ analysis_volume: 0 }),
  investor:     Object.freeze({ analysis_volume: null }),
  professional: Object.freeze({ analysis_volume: null }),
  enterprise:   Object.freeze({ analysis_volume: null }),
});

// BL-040 (ADR-051): trial analysis ceiling = 5 ("7 days OR 5 analyses").
export const TRIAL_LIMITS = Object.freeze({ analysis_volume: 5 });

function normalizeFlags(featureFlags) {
  if (!featureFlags) return {};
  if (Array.isArray(featureFlags)) {
    const map = {};
    for (const row of featureFlags) {
      if (row && row.flag_key) map[row.flag_key] = row;
    }
    return map;
  }
  return featureFlags;
}

function resolveCapability(def, ctx, flagMap) {
  const tierRank = TIER_RANK[ctx.tier];
  const minRank = TIER_RANK[def.minTier];

  if (def.requiresFlag) {
    const flag = flagMap[def.requiresFlag];
    if (!flag || !flag.enabled) return false;
    const tiers = flag.enabled_for_tiers || [];
    if (tiers.length > 0) return tiers.includes(ctx.tier);
    return tierRank !== undefined && tierRank >= minRank;
  }

  const tierGrants = ctx.status === "active" && tierRank !== undefined && tierRank >= minRank;
  const trialGrant = def.trialGrants && ctx.isTrialing;
  return tierGrants || trialGrant;
}

export function getEntitlementContext(profile, opts = {}) {
  const now = opts.now ?? Date.now();
  const flagMap = normalizeFlags(opts.featureFlags);

  const tier = profile?.subscription_tier || "free";
  const status = profile?.subscription_status || "trialing";

  const trialEndsAt = profile?.trial_ends_at;
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - now) / DAY_MS))
    : 0;

  const isTrialing = status === "trialing" && trialDaysLeft > 0;
  const isInvestor = status === "active" && tier === "investor";
  const isProfessional = status === "active" && tier === "professional";
  const isEnterprise = status === "active" && tier === "enterprise";
  const isPro = isInvestor || isProfessional || isEnterprise;
  const isExpired = !isPro && !isTrialing;

  const baseCtx = { tier, status, isTrialing, isPro };
  const capabilities = {};
  for (const [key, def] of Object.entries(CAPABILITIES)) {
    capabilities[key] = resolveCapability(def, baseCtx, flagMap);
  }

  const limits = isTrialing
    ? { ...TRIAL_LIMITS }
    : { ...(TIER_LIMITS[tier] || TIER_LIMITS.free) };

  return {
    tier,
    status,
    trialDaysLeft,
    isTrialing,
    isInvestor,
    isProfessional,
    isEnterprise,
    isPro,
    isExpired,
    capabilities,
    limits,
  };
}

export function checkEntitlement(profile, capabilityKey, opts = {}) {
  const ctx = getEntitlementContext(profile, opts);
  return ctx.capabilities[capabilityKey] === true;
}

export function checkLimit(context, limitKey, currentUsage = 0) {
  const limit = context?.limits?.[limitKey];
  if (limit === null || limit === undefined) {
    return { allowed: true, unlimited: true, limit: null, remaining: null };
  }
  const used = Number(currentUsage) || 0;
  const remaining = Math.max(0, limit - used);
  return { allowed: used < limit, unlimited: false, limit, remaining };
}
// <<< BL-041 PARITY REGION END
